import {
  CREATABLE_USER_TYPES,
  USER_TYPE_LABELS,
  UNCLASSIFIED_LOGIN_USER_TYPES,
  normalizeRoleCategory as normalizeUserType,
  getRoleCategoryLabel as getUserTypeLabel,
  getAllowedCreatableUserTypes,
} from '../../../utils/roleCategoryUtils';

export {
  CREATABLE_USER_TYPES,
  USER_TYPE_LABELS,
  UNCLASSIFIED_LOGIN_USER_TYPES,
  normalizeUserType,
  getUserTypeLabel,
  getAllowedCreatableUserTypes,
};

/** User Type field whose options follow the logged-in account type. */
const buildUserTypeField = (currentUserType) => {
  const loginType = normalizeUserType(currentUserType);
  const allowed = getAllowedCreatableUserTypes(currentUserType);
  const values = allowed === null ? UNCLASSIFIED_LOGIN_USER_TYPES : allowed;

  return {
    name: 'userType',
    label: 'User Type',
    type: 'select',
    required: true,
    defaultValue: values[0] || '',
    options: values.map((value) => ({ value, label: getUserTypeLabel(value) })),
    hint:
      allowed === null
        ? undefined
        : `Logged in as ${getUserTypeLabel(loginType)}: only ${values
            .map(getUserTypeLabel)
            .join(' / ')} accounts can be created here.`,
  };
};

/**
 * User-creation form fields for the logged-in user type.
 *
 * @param {string|null|undefined} currentUserType userType of the logged-in
 *   account (GET /auth/about-me). When it is unknown the User Type dropdown
 *   keeps the standard option list instead of the hierarchy-restricted one.
 */
export const buildUserRegistrationFields = (currentUserType) => [
  {
    name: 'name',
    label: 'Full Name',
    type: 'text',
    required: true,
    placeholder: 'User full name',
  },
  {
    name: 'userMail',
    label: 'Email',
    type: 'email',
    required: true,
    maxLength: 150,
    placeholder: 'user@company.com',
  },
  {
    name: 'mobileNo',
    label: 'Mobile Number',
    type: 'tel',
    required: true,
    maxLength: 10,
    minLength: 10,
    placeholder: '10-digit mobile number',
  },
  buildUserTypeField(currentUserType),
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    required: true,
    defaultValue: 'ACTIVE',
    options: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'INACTIVE', label: 'Inactive' },
    ],
  },
];


// employeeCode and loginId are system-generated by the backend and are not
// part of the employee registration form.
export const employeeRegistrationFields = [
  {
    name: 'firstName',
    label: 'First Name',
    type: 'text',
    required: true,
    placeholder: 'First name',
  },
  {
    name: 'lastName',
    label: 'Last Name',
    type: 'text',
    placeholder: 'Last name',
  },
  {
    name: 'email',
    label: 'Email',
    type: 'email',
    required: true,
    maxLength: 150,
    placeholder: 'employee@company.com',
  },
  {
    name: 'mobileNo',
    label: 'Mobile Number',
    type: 'tel',
    required: true,
    maxLength: 10,
    minLength: 10,
    placeholder: '10-digit number',
  },
  {
    name: 'dateOfBirth',
    label: 'Date of Birth',
    type: 'date',
  },
  {
    name: 'dateOfJoining',
    label: 'Date of Joining',
    type: 'date',
    required: true,
  },
  {
    name: 'department',
    label: 'Department',
    type: 'text',
    placeholder: 'e.g. Engineering',
  },
  {
    name: 'designation',
    label: 'Designation',
    type: 'text',
    placeholder: 'e.g. Software Engineer',
  },
  {
    name: 'reportingManagerId',
    label: 'Reporting Manager ID',
    type: 'number',
    placeholder: 'Employee ID of manager',
    hint: 'Leave empty for top-level roles',
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    required: true,
    defaultValue: 'ACTIVE',
    options: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'INACTIVE', label: 'Inactive' },
      { value: 'RESIGNED', label: 'Resigned' },
      { value: 'TERMINATED', label: 'Terminated' },
    ],
  },
];

export const clientRegistrationFields = [
  {
    name: 'clientId',
    label: 'Client ID',
    type: 'text',
    required: true,
    placeholder: 'Unique client identifier',
  },
  {
    name: 'clientName',
    label: 'Client Name',
    type: 'text',
    required: true,
    placeholder: 'Organization name',
  },
  {
    name: 'url',
    label: 'URL',
    type: 'text',
    placeholder: 'https://client.example.com',
    colSpan: 2,
  },
  {
    name: 'username',
    label: 'Username',
    type: 'text',
    placeholder: 'Client portal username',
  },
  {
    name: 'password',
    label: 'Password',
    type: 'password',
    placeholder: 'Client portal password',
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    defaultValue: 'ACTIVE',
    options: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'INACTIVE', label: 'Inactive' },
    ],
  },
];
